/**
 * SYNAPZ Launch Network — global network registry.
 *
 * Single source of truth for all supported and planned blockchains.
 * Active chains (Robinhood Testnet) also have a full ChainConfig in chains.ts;
 * entries here carry the public-facing roadmap data used by the UI.
 *
 * Execution order (owner-approved):
 *   1. Robinhood Chain (live testnet)
 *   2. Base          (coming next)
 *   3. Solana        (coming next, separate implementation)
 *   4. HyperEVM      (coming soon)
 *   5. Sui           (coming soon)
 *   6. XRP Ledger    (coming soon)
 *   7. TRON          (coming soon)
 *   8. Dogecoin      (under evaluation)
 *
 * Rule: never display an undeployed network as live.
 * Rule: unavailable networks may show roadmap info only — no launch buttons.
 */

// ── Status types ─────────────────────────────────────────────────────────────

export type NetworkStatus =
  | "live-testnet"        // deployed and verified on testnet
  | "live-mainnet"        // deployed and verified on mainnet
  | "next-major-network"  // research/spec complete; implementation begins after current chain stabilises
  | "in-development"      // active platform-level development (e.g. SYNAPZ native chain)
  | "coming-next"         // development begins after current chain stabilises
  | "coming-soon"         // planned but not yet in development
  | "under-evaluation";   // feasibility not yet determined

export type NetworkProduct = "standard-token" | "fair-launch";

export type NetworkVm = "evm" | "svm" | "move" | "xrpl" | "utxo" | "tvm";

// ── Network entry ─────────────────────────────────────────────────────────────

export interface NetworkEntry {
  /** Internal key — stable identifier, never shown directly in UI. */
  readonly key: string;
  /** Display name shown in the selector and network cards. */
  readonly name: string;
  /** Short name for compact UI (selector pills, breadcrumbs). */
  readonly shortName: string;
  /**
   * Route slug used in chain-aware URLs like /launch/:chain.
   * null for chains not yet router-integrated.
   */
  readonly slug: string | null;
  /** EVM chain ID, or null for non-EVM chains. */
  readonly chainId: number | null;
  /** Native gas asset display name. */
  readonly nativeAsset: string;
  /** Ticker symbol for the native asset. */
  readonly nativeAssetSymbol: string;
  /** VM / execution environment. Determines implementation strategy. */
  readonly vm: NetworkVm;
  /** Public-facing status. Drives badge rendering and availability gating. */
  readonly status: NetworkStatus;
  /** Products actually deployed and verified on this network. */
  readonly products: readonly NetworkProduct[];
  /** Products planned but not yet available. Shown as "planned" in UI. */
  readonly plannedProducts: readonly NetworkProduct[];
  /** Human-readable one-line status note shown on roadmap cards. */
  readonly statusNote: string;
  /**
   * Whether a user can initiate a launch on this network in the current build.
   * Must be false for any network without a live, verified factory deployment.
   */
  readonly launchEnabled: boolean;
  /** Testnet explorer base URL, or null if unavailable. */
  readonly testnetExplorerUrl?: string;
  /** Whether this is a separate, non-EVM implementation (e.g. Solana). */
  readonly requiresSeparateImplementation: boolean;
  /**
   * Accent color for UI theming (border, badge, icon fill).
   * WCAG-compliant against the void (#05060a) background.
   */
  readonly accentColor: string;
}

// ── Registry ──────────────────────────────────────────────────────────────────

export const NETWORK_REGISTRY: readonly NetworkEntry[] = [
  // ── 1. Robinhood Chain — first supported launch network ──────────────────────
  {
    key: "robinhoodTestnet",
    name: "Robinhood Chain",
    shortName: "Robinhood",
    slug: "robinhood",
    chainId: 46630,
    nativeAsset: "Ether",
    nativeAssetSymbol: "ETH",
    vm: "evm",
    status: "live-testnet",
    products: ["standard-token", "fair-launch"],
    plannedProducts: [],
    statusNote: "Fair Launch live on testnet. Mainnet deployment pending stability gate.",
    launchEnabled: true,
    testnetExplorerUrl: "https://explorer.testnet.chain.robinhood.com",
    requiresSeparateImplementation: false,
    accentColor: "#00e5c7", // plasma cyan
  },
  // ── 2. Solana — next major network ───────────────────────────────────────────
  {
    key: "solana",
    name: "Solana",
    shortName: "Solana",
    slug: "solana",
    chainId: null,
    nativeAsset: "Solana",
    nativeAssetSymbol: "SOL",
    vm: "svm",
    status: "next-major-network",
    products: [],
    plannedProducts: ["standard-token", "fair-launch"],
    statusNote: "Research and specification complete. Production implementation paused until Robinhood mainnet is stable.",
    launchEnabled: false,
    requiresSeparateImplementation: true,
    accentColor: "#9945ff", // Solana purple
  },
  // ── 3. Base — planned EVM expansion ──────────────────────────────────────────
  {
    key: "base",
    name: "Base",
    shortName: "Base",
    slug: "base",
    chainId: 8453,
    nativeAsset: "Ether",
    nativeAssetSymbol: "ETH",
    vm: "evm",
    status: "coming-soon",
    products: [],
    plannedProducts: ["standard-token", "fair-launch"],
    statusNote: "Planned EVM expansion. Development begins after Robinhood stability gate.",
    launchEnabled: false,
    requiresSeparateImplementation: false,
    accentColor: "#0052ff", // Base blue
  },
  // ── 4. Ethereum — planned ─────────────────────────────────────────────────────
  {
    key: "ethereum",
    name: "Ethereum",
    shortName: "Ethereum",
    slug: null,
    chainId: 1,
    nativeAsset: "Ether",
    nativeAssetSymbol: "ETH",
    vm: "evm",
    status: "coming-soon",
    products: [],
    plannedProducts: ["standard-token", "fair-launch"],
    statusNote: "Planned. Timing depends on gas conditions and Robinhood graduation flow.",
    launchEnabled: false,
    requiresSeparateImplementation: false,
    accentColor: "#627eea", // Ethereum blue
  },
  // ── 5. SYNAPZ Chain — in development ─────────────────────────────────────────
  {
    key: "synapzChain",
    name: "SYNAPZ Chain",
    shortName: "SYNAPZ",
    slug: null,
    chainId: null,
    nativeAsset: "SYNAPZ",
    nativeAssetSymbol: "SYN",
    vm: "evm",
    status: "in-development",
    products: [],
    plannedProducts: ["standard-token", "fair-launch"],
    statusNote: "SYNAPZ native chain. In development as the long-term home for the SYNAPZ ecosystem.",
    launchEnabled: false,
    requiresSeparateImplementation: false,
    accentColor: "#b94dff", // SYNAPZ purple
  },
  {
    key: "hyperEvm",
    name: "HyperEVM",
    shortName: "HyperEVM",
    slug: null,
    chainId: null,
    nativeAsset: "HYPE",
    nativeAssetSymbol: "HYPE",
    vm: "evm",
    status: "coming-soon",
    products: [],
    plannedProducts: ["standard-token", "fair-launch"],
    statusNote: "Coming soon.",
    launchEnabled: false,
    requiresSeparateImplementation: false,
    accentColor: "#ff6b00",
  },
  {
    key: "sui",
    name: "Sui",
    shortName: "Sui",
    slug: null,
    chainId: null,
    nativeAsset: "Sui",
    nativeAssetSymbol: "SUI",
    vm: "move",
    status: "coming-soon",
    products: [],
    plannedProducts: ["standard-token", "fair-launch"],
    statusNote: "Coming soon.",
    launchEnabled: false,
    requiresSeparateImplementation: true,
    accentColor: "#6fbcf0", // Sui blue
  },
  {
    key: "xrpLedger",
    name: "XRP Ledger",
    shortName: "XRPL",
    slug: null,
    chainId: null,
    nativeAsset: "XRP",
    nativeAssetSymbol: "XRP",
    vm: "xrpl",
    status: "coming-soon",
    products: [],
    plannedProducts: [],
    statusNote: "Coming soon.",
    launchEnabled: false,
    requiresSeparateImplementation: true,
    accentColor: "#00aae4",
  },
  {
    key: "tron",
    name: "TRON",
    shortName: "TRON",
    slug: null,
    chainId: null,
    nativeAsset: "TRON",
    nativeAssetSymbol: "TRX",
    vm: "tvm",
    status: "coming-soon",
    products: [],
    plannedProducts: [],
    statusNote: "Coming soon.",
    launchEnabled: false,
    requiresSeparateImplementation: true,
    accentColor: "#ef0027",
  },
  {
    key: "dogecoin",
    name: "Dogecoin Ecosystem",
    shortName: "Dogecoin",
    slug: null,
    chainId: null,
    nativeAsset: "Dogecoin",
    nativeAssetSymbol: "DOGE",
    vm: "utxo",
    status: "under-evaluation",
    products: [],
    plannedProducts: [],
    // Owner decision: do not imply native Dogecoin smart-contract support is solved
    statusNote: "Under evaluation. Native smart-contract support not yet determined.",
    launchEnabled: false,
    requiresSeparateImplementation: true,
    accentColor: "#c2a633",
  },
] as const;

// ── Derived helpers ───────────────────────────────────────────────────────────

/** The active network entry for the current build. */
export const ACTIVE_NETWORK: NetworkEntry = NETWORK_REGISTRY[0]!;

/** Networks with launchEnabled=true. */
export const LIVE_NETWORKS = NETWORK_REGISTRY.filter((n) => n.launchEnabled);

/** Human-readable status badge labels. */
export const STATUS_LABELS: Record<NetworkStatus, string> = {
  "live-testnet":        "LIVE TESTNET",
  "live-mainnet":        "LIVE",
  "next-major-network":  "NEXT MAJOR NETWORK",
  "in-development":      "IN DEVELOPMENT",
  "coming-next":         "COMING NEXT",
  "coming-soon":         "COMING SOON",
  "under-evaluation":    "UNDER EVALUATION",
};

/** Badge tone (maps to ui.tsx Pill tone prop). */
export const STATUS_TONES: Record<NetworkStatus, "plasma" | "warn" | "danger" | "muted"> = {
  "live-testnet":        "plasma",
  "live-mainnet":        "plasma",
  "next-major-network":  "warn",
  "in-development":      "warn",
  "coming-next":         "warn",
  "coming-soon":         "muted",
  "under-evaluation":    "muted",
};

export const PRODUCT_LABELS: Record<NetworkProduct, string> = {
  "standard-token": "Standard Token",
  "fair-launch":    "Fair Launch",
};
